import AllAgentApplicationsScreen from '@/components/Home/AllAgentApplicationsScreen.js';
import { Button } from '@/components/ui/button';
import homePaths from '@/constants/homePaths';
import {Plus, RefreshCcw, Sparkles, ArrowRight} from 'lucide-react';
import React, {useRef} from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {formatTitle} from "@/helpers/helperFunctions.js";
import HelpDialog from "@/components/HelpDialog.js";
import {useUser} from "@clerk/clerk-react";

const Home = () => {
  const applicationsRef = useRef();
  const { user } = useUser();
  const navigate = useNavigate();

  const refreshingApplications = async () => {
    toast.info('Applications will refresh soon');
    applicationsRef.current.refresh();
  };

  const handleNewApplication = () => {
    navigate([homePaths.home, homePaths.new].join(''), {
      replace: true,
    });
  };

  const displayName = user?.firstName;

  return (
    <div className='space-y-4'>
      {/* Ask Kay AI Banner */}
      <a
        href="https://ask.kay.ai?ref=user-app"
        target="_blank"
        rel="noopener noreferrer"
        className="group relative block overflow-hidden rounded-xl bg-gradient-to-r from-kay-main/40 via-kay-main/30 to-kay-main/40 border border-kay-main/60 shadow-md hover:shadow-lg hover:from-kay-main/50 hover:via-kay-main/40 hover:to-kay-main/50 transition-all duration-300"
      >
        {/* Decorative pattern overlay */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, rgb(0,0,0) 1px, transparent 0)',
            backgroundSize: '24px 24px'
          }} />
        </div>
        
        <div className="relative px-5 py-3.5 md:px-6 md:py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3.5 flex-1 min-w-0">
            <div className="flex-shrink-0">
              <div className="p-2.5 bg-white/90 backdrop-blur-sm rounded-full group-hover:bg-white transition-colors duration-200 shadow-sm">
                <Sparkles className="h-4.5 w-4.5 md:h-5 md:w-5 text-kay-black" strokeWidth={2.5} />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-slate-900 font-bold text-base md:text-lg leading-tight">
                Looking for <span className="text-kay-black">Ask Kay</span>
              </p>
              <p className="text-slate-700 text-xs md:text-sm mt-0.5 font-medium">
                Get instant answers from Kay AI
              </p>
            </div>
          </div>
          <div className="flex-shrink-0">
            <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg group-hover:bg-slate-50 transition-all duration-200 shadow-sm border border-slate-200/50">
              <span className="text-sm font-semibold text-slate-900 hidden sm:inline">Visit</span>
              <ArrowRight className="h-4 w-4 md:h-4.5 md:w-4.5 text-slate-900 group-hover:translate-x-0.5 transition-transform duration-200" strokeWidth={2.5} />
            </div>
          </div>
        </div>
        
        {/* Left accent bar */}
        <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b from-kay-main via-kay to-kay-main" />
      </a>

      <div className='flex items-center justify-between space-y-3'>
        <div>
          <h2 className='text-2xl md:text-3xl'>
            { displayName ? `Hello, ${formatTitle(displayName)}!` : 'Hello!' }
          </h2>
        </div>

        <div className='w-fit'>
          <HelpDialog />
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="inline-block font-medium text-lg md:text-xl">
            Applications
          </h4>
          <div className="space-x-3">
            <Button
              className="border hover:border-black space-x-2"
              onClick={handleNewApplication}
              variant="kay"
            >
              <Plus className="h-4 w-4" />
              <span>New Application</span>
            </Button>
            <Button
              className="border hover:border-black space-x-2"
              onClick={refreshingApplications}
              variant="outline"
            >
              <RefreshCcw className='h-4 w-4' />
            </Button>
          </div>
        </div>

        <AllAgentApplicationsScreen ref={applicationsRef} runningOnly={true} />
      </div>
    </div>
  )
};

export default Home;
